import React, { useState } from "react";
import "./ProductCard.scss";
import { theme, Card, Image, Button, Rate, Typography, message } from "antd";
import { ReactComponent as StarIcon } from "../../assets/startIcon.svg";
import {
  HeartOutlined,
  HeartFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  addFavorite,
  removeFavorite,
} from "../../features/favorites/favoritesActions";
import useWindowWidth from "../../hooks/useWindowWidth";
import product_logo from "../../assets/logo_product.svg";
import useCard from "../../hooks/useCard";
import VideoPreview from "../VideoPreview/VideoPreview";
import Utils from "../../utils";
import RolesHelper from "../../common/services/permissions/RolesHelper";
import { LOW_STOCK_THRESHOLD } from "../../config/constant";

export default function ProductCard({
  product,
  navigate,
  haveDrawer,
  openDrawer,
  disabled,
  onFavoriteToggled,
  layout,
}) {
  // "list" draws the same card as a row; the markup and every handler are
  // shared, only the sizes below and the scss modifier differ.
  const isList = layout === "list";
  const btnStyle = isList ? { width: "100%" } : { flex: 1, minWidth: 0 };
  // The card rounds the top of its cover, the row rounds all four corners.
  // Always the four corner properties, never the shorthand: React warns when
  // the two kinds are swapped on a rerender.
  const coverRadius = {
    borderTopRightRadius: "8px",
    borderTopLeftRadius: "8px",
    borderBottomRightRadius: isList ? "8px" : 0,
    borderBottomLeftRadius: isList ? "8px" : 0,
  };
  const { useToken } = theme;
  const { token } = useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const openSuccess = () => {
    messageApi.open({
      type: "success",
      content: "Product added to cart",
    });
  };
  const openError = () => {
    messageApi.open({
      type: "error",
      content: "Failed to add product to cart",
    });
  };
  const { addToCard } = useCard();
  const { width } = useWindowWidth();
  // The `navigate` prop goes to the single product page; checkout needs the
  // router directly.
  const routerNavigate = useNavigate();
  const [addedVariant, setAddedVariant] = useState(null);
  const [adding, setAdding] = useState(false);
  const [buying, setBuying] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);
  const parentCategory = Utils?.findParentCategory(product);
  // Buy Now skips the drawer/variant choice entirely: first in-stock variant.
  const firstAvailableVariant = Utils?.findVariantCountGt0(
    product?.variants?.length ? product?.variants : []
  );
  const pageVisible = RolesHelper?.getPagesVisibility();
  const dispatch = useDispatch();
  // product?.is_favorite rides every catalog/favorites read but goes stale in
  // the menuItems slice after a toggle — overrides record the server-confirmed
  // flag of every toggle this session, so they win when present.
  const overrides = useSelector((state) => state?.favorites?.overrides);
  const isFavorite = overrides?.[product?.id] ?? Boolean(product?.is_favorite);
  // Shop setting "Show stock counts on shop" (CANA-639); missing key = off.
  const shop = useSelector((state) => state?.shopDetails?.shop);
  const isCountVisible = Boolean(shop?.is_count_visible_on_shop);
  // Right-aligned per variant line; hidden on an unavailable card because
  // stock is meaningless there. Low stock switches to the warning colour.
  const renderVariantCount = (variant) => {
    if (disabled || !isCountVisible) return null;
    const label = Utils?.showVariantCount(variant);
    if (!label) return null;
    const isLow = Number(variant?.count) <= LOW_STOCK_THRESHOLD;
    // Rendered as a small line under the price (see scss), so the price
    // text keeps the width it always had.
    return (
      <Typography.Text
        className={`productCard__body__variantWrapper__line__count${
          isLow ? " productCard__body__variantWrapper__line__count--low" : ""
        }`}
        style={{
          color: isLow ? token?.colorWarning : token?.colorTextTertiary,
        }}>
        {label}
      </Typography.Text>
    );
  };
  // product?.rate goes stale the same way after a rating submit — the reviews
  // overrides carry the fresh item average the server returned with the
  // write, so the widget always shows the average (never the own rating).
  const reviewOverride = useSelector((state) => state?.reviews?.overrides)?.[
    product?.id
  ];
  const shownRate = reviewOverride
    ? reviewOverride?.rate ?? 0
    : product?.rate ?? 0;
  const shownReviewsCount = reviewOverride
    ? reviewOverride?.reviews_count ?? 0
    : product?.reviews_count ?? 0;

  return (
    <>
      {contextHolder}
      <Card
        key={`${product?.id}`}
        id={`${product?.id}`}
        className={isList ? "productCard productCard--list" : "productCard"}
        hoverable={!disabled}
        style={{
          border: "none",
          backgroundColor: token?.Card?.colorBgCard,
          height: "100%",
          maxHeight: !isList && width > 2000 ? "373px" : "auto",
          cursor: disabled ? "not-allowed" : undefined,
        }}
        styles={{
          body: {
            padding: "0px",
            width: "100%",
            height: "100%",
          },
          cover: isList
            ? {}
            : {
                width: "100%",
                height: "170px",
              },
        }}
        cover={
          <div
            className="productCard__coverWrapper"
            style={
              !product?.picture_url &&
              !(product?.video_ready && product?.video_hls_url)
                ? {
                    background: token?.product_default_image_bg,
                    ...coverRadius,
                  }
                : null
            }>
            {product?.video_ready && product?.video_hls_url ? (
              <VideoPreview
                className="productCard__coverWrapper__video"
                hlsUrl={product?.video_hls_url}
                thumbnailUrl={product?.video_thumbnail_url}
                ariaLabel={product?.name || "Product preview"}
              />
            ) : (
              <Image
                style={
                  !product?.picture_url && !product?.video_thumbnail_url
                    ? {
                        width: "64px",
                        height: "38px",
                      }
                    : {
                        width: "100%",
                        height: isList ? "100%" : "170px",
                        objectFit: isList ? "cover" : undefined,
                        ...coverRadius,
                      }
                }
                alt="example"
                src={
                  product?.picture_url
                    ? product?.picture_url
                    : product?.video_thumbnail_url
                    ? product?.video_thumbnail_url
                    : product_logo
                }
                preview={false}
              />
            )}
            {pageVisible?.is_favorites_visible ? (
              <Button
                className="productCard__coverWrapper__btn"
                type="text"
                size="small"
                loading={
                  togglingFavorite && {
                    // Brand-colored spinner in the heart's exact slot, so the
                    // icon swap causes no size or tone shift.
                    icon: (
                      <LoadingOutlined
                        spin
                        style={{
                          color: token?.icons?.colorPrimaryIcon,
                          fontSize: "24px",
                        }}
                      />
                    ),
                  }
                }
                icon={
                  isFavorite ? (
                    <HeartFilled
                      style={{
                        color: token?.icons?.colorPrimaryIcon,
                        fontSize: "24px",
                      }}
                    />
                  ) : (
                    <HeartOutlined
                      style={{
                        color: token?.icons?.colorPrimaryIcon,
                        fontSize: "24px",
                      }}
                    />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  const result = dispatch(
                    isFavorite
                      ? removeFavorite(product?.id)
                      : addFavorite(product?.id)
                  );
                  if (typeof result?.then === "function") {
                    setTogglingFavorite(true);
                    result.then((res) => {
                      setTogglingFavorite(false);
                      // Only the Favorites page passes this — a toggle there
                      // changes the visible list, so it refetches its page.
                      if (
                        typeof onFavoriteToggled === "function" &&
                        res?.type?.endsWith("fulfilled")
                      ) {
                        onFavoriteToggled();
                      }
                    });
                  }
                }}
              />
            ) : null}
          </div>
        }
        onClick={() => {
          // Unavailable items have no working single-product page, so a
          // grayed-out card must not navigate anywhere.
          if (disabled) {
            return;
          }
          if (typeof navigate === "function") {
            navigate();
          }
        }}>
        <div className="productCard__body">
          <Typography.Text className="productCard__body__text">
            Category: {parentCategory ? parentCategory?.name : ""}
          </Typography.Text>
          <Typography.Paragraph
            className="productCard__body__bigText"
            style={{
              color: token?.Typography?.mainColorText,
              margin: 0,
            }}
            ellipsis={{
              rows: 2,
              tooltip: {
                title: product?.name || "",
              },
            }}>
            {product?.name || ""}
          </Typography.Paragraph>
          <div className="productCard__body__rateWrapper">
            <Rate
              // Display-only, marketplace-style: the community average with
              // its count. Rating happens in the product page reviews tab;
              // half-star steps — averages like 3.5 show a half star, 4.3
              // snaps to 4.5, the exact value is the number beside.
              allowHalf
              value={Utils?.roundRateToHalf(shownRate)}
              disabled
              character={
                <StarIcon
                  style={{
                    fontSize: "12px",
                  }}
                />
              }
              count={5}
              style={{
                fontSize: "12px",
              }}
            />
            <Typography.Text className="productCard__body__text">
              {shownReviewsCount
                ? `${shownRate} (${shownReviewsCount})`
                : shownRate}
            </Typography.Text>
          </div>
          <Typography.Text className="productCard__body__text">
            {`Thc : ${
              product?.cannabinoids?.thc ? `${product?.cannabinoids?.thc}%` : ""
            }`}
          </Typography.Text>
          <div className="productCard__body__variantWrapper">
            {Utils?.getMinandMaxVariants(
              product?.variants?.length ? product?.variants : []
            )?.map((variant) => {
              const currentTime = Date.now(); // Get current time in milliseconds
              const randomNumber = Math.floor(Math.random() * 1000) + 1; // Generate random number between 1 and 1000
              const uniqueNumber = currentTime + randomNumber;
              const countNode = renderVariantCount(variant);
              const weightLabel =
                variant?.name && variant?.unit
                  ? `${Utils?.showProductDetail(variant)} - `
                  : "";
              const isOnSale =
                variant?.price &&
                variant?.sale_price &&
                Utils?.isSalePriceValid(variant?.sale_price, variant?.price);
              const priceText = variant?.price
                ? `${weightLabel} ${Utils?.roundNumber(variant?.price)}`
                : "$";
              const saleText = variant?.sale_price
                ? ` ${weightLabel} ${Utils?.roundNumber(variant?.sale_price)}`
                : "$";
              return isOnSale ? (
                <div
                  className="productCard__body__variantWrapper__line"
                  key={variant?.id}>
                  <Typography.Text className="productCard__body__text">
                    <Typography.Text
                      className="productCard__body__text"
                      style={{
                        color: token?.Typography?.colorPrimaryText,
                      }}
                      delete>
                      {variant?.price
                        ? `${Utils?.roundNumber(variant?.price)}`
                        : "$"}
                    </Typography.Text>
                    {saleText}
                  </Typography.Text>
                  {countNode}
                </div>
              ) : (
                <div
                  className="productCard__body__variantWrapper__line"
                  key={variant?.id ? variant?.id : uniqueNumber}>
                  <Typography.Text className="productCard__body__text">
                    {priceText}
                  </Typography.Text>
                  {countNode}
                </div>
              );
            })}
          </div>
          <div className="productCard__body__btnRow">
            <Button
              style={btnStyle}
              loading={adding}
              disabled={
                disabled ||
                (product?.variants?.length === 1 &&
                  addedVariant?.id?.toString() ===
                    product?.variants[0]?.id?.toString())
              }
              onClick={(e) => {
                e.stopPropagation();
                if (product?.variants?.length === 1) {
                  setAddedVariant(product?.variants[0]);
                  const result = addToCard(product?.variants[0], 1, product);
                  if (typeof result?.then === "function") {
                    // Authenticated flow: the item is only in the cart once the
                    // server confirms — hold the loading state until then.
                    setAdding(true);
                    result.then((res) => {
                      setAdding(false);
                      setAddedVariant(null);
                      if (res?.type === "basket/set_item/fulfilled") {
                        openSuccess();
                      } else {
                        openError();
                      }
                    });
                  } else {
                    openSuccess();
                    setTimeout(() => {
                      setAddedVariant(null);
                    }, 1500);
                  }
                } else if (typeof navigate === "function" && !haveDrawer) {
                  navigate();
                } else if (typeof openDrawer === "function" && haveDrawer) {
                  openDrawer(product);
                }
              }}>
              Add to Cart
            </Button>
            <Button
              type="primary"
              style={btnStyle}
              loading={buying}
              disabled={disabled || !firstAvailableVariant}
              onClick={(e) => {
                e.stopPropagation();
                const result = addToCard(firstAvailableVariant, 1, product);
                if (typeof result?.then === "function") {
                  // Authenticated flow: navigate only after the server
                  // confirms the item is in the basket.
                  setBuying(true);
                  result.then((res) => {
                    setBuying(false);
                    if (res?.type === "basket/set_item/fulfilled") {
                      routerNavigate("/checkout");
                    } else {
                      openError();
                    }
                  });
                } else {
                  routerNavigate("/checkout");
                }
              }}>
              Buy Now
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
}
